import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { Calendar, DateData } from 'react-native-calendars';
import { getIntl } from '../../../../../utils/intl';
import { colors } from '../../../../../utils/theme';
import { getSpacing } from '../../../../../utils/spacing';
import { PromptActions, PromptContainer } from '../../../../v2BaseComponents/Prompt';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';
import { PRScrollView } from '../../../Scroll';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import Icon from '../../../../v2BaseComponents/Icon';
import PressableDynamicOpacity from '../../../../v2BaseComponents/PressableDynamicOpacity';
import Switch from '../../../../v2BaseComponents/Inputs/Switch';
import TimeListField from '../../../../v2BaseComponents/Inputs/DateTimePicker/TimeListField';
import {
    addMinutesToISO,
    addMinutesToTimeSameDay,
    combineDateAndTime,
    formatWhenLabel,
    isEndAfterStart,
    splitISO,
} from './utils';

const intl = getIntl('VisitWhenPicker', {
    when: 'When',
    promptTitle: 'Select Date & Time',
    startTime: 'Start Time',
    endTime: 'End Time',
    allDay: 'All day',
    set: 'Set',
    cancel: 'Cancel',
    endMustBeAfterStart: 'End must be after start',
});

const DEFAULT_VISIT_MINUTES = 60;
const ALL_DAY_START_TIME = '00:00';
const ALL_DAY_END_TIME = '23:59';

export type VisitWhenPickerProps = {
    startDateTime?: string | null;
    endDateTime?: string | null;
    duration: string | null;
    isDisabled?: boolean;
    setFieldValue: (field: string, value: any) => void;
    /** Open the picker immediately on mount (e.g. straight from the Share Visit action). */
    autoOpen?: boolean;
};

type DraftState = {
    date: string | null;
    startTime: string | null;
    endTime: string | null;
    isAllDay: boolean;
};

const getDraftFromProps = (props: VisitWhenPickerProps): DraftState => {
    const start = props.startDateTime ? splitISO(props.startDateTime) : null;
    // Legacy resources may carry a duration instead of an explicit end.
    const endISO =
        props.endDateTime ||
        (props.startDateTime && props.duration ? addMinutesToISO(props.startDateTime, parseInt(props.duration)) : null);
    const end = endISO ? splitISO(endISO) : null;
    // Only infer all-day from an explicit 00:00–23:59 pair (a bare 00:00 start may be a
    // date-only visit).
    const isAllDay = !!start && start.time === ALL_DAY_START_TIME && end?.time === ALL_DAY_END_TIME;
    return {
        date: start?.date || null,
        startTime: !isAllDay && start ? start.time : null,
        endTime: !isAllDay && end ? end.time : null,
        isAllDay,
    };
};

const TimeFieldsLayout: React.FC<React.PropsWithChildren<{ isMobile: boolean }>> = ({ isMobile, children }) =>
    isMobile ? (
        <Stack vertical fullWidth space={getSpacing(4)}>
            {children}
        </Stack>
    ) : (
        <Stack horizontal fullWidth space={getSpacing(3)} align="flex-start">
            {children}
        </Stack>
    );

const calendarTheme = {
    selectedDayBackgroundColor: colors.Primary,
    todayTextColor: colors.Primary,
    arrowColor: colors.Primary,
};

const VisitWhenPicker: React.FC<VisitWhenPickerProps> = (props) => {
    const [isOpen, setIsOpen] = useState(!!props.autoOpen);
    const [draft, setDraft] = useState<DraftState>(() => getDraftFromProps(props));
    const isMobile = useIsMobileLayout();

    useEffect(() => {
        if (isOpen) setDraft(getDraftFromProps(props));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const showTimeSelects = !draft.isAllDay;

    const draftStartISO = draft.date && draft.startTime ? combineDateAndTime(draft.date, draft.startTime) : null;
    const draftEndISO = draft.endTime && draft.date ? combineDateAndTime(draft.date, draft.endTime) : null;

    const endInvalid = !!(draftStartISO && draftEndISO && !isEndAfterStart(draftStartISO, draftEndISO));

    // Timed (non-all-day) visits require BOTH a start and a valid end: writing an
    // undefined end is dropped from the request and the backend keeps the stale end,
    // which could then precede the new start. Exception: when no end was ever
    // persisted (legacy start-only visits) there is no stale end to corrupt, so the
    // date can be changed without inventing one.
    const hasPersistedEnd = !!props.endDateTime || !!props.duration;
    const canSet =
        !!draft.date && (draft.isAllDay || (!!draft.startTime && (!!draft.endTime || !hasPersistedEnd))) && !endInvalid;

    const markedDates = useMemo(() => {
        if (!draft.date) return {};
        return { [draft.date]: { selected: true, selectedColor: colors.Primary } };
    }, [draft.date]);

    const handleDayPress = (day: DateData) => {
        setDraft((prev) => ({ ...prev, date: day.dateString }));
    };

    const handleSet = () => {
        if (!draft.date) return;

        // The duration selector is retired (PR-1913) — always persist an explicit end.
        props.setFieldValue('resource.duration', null);

        if (draft.isAllDay || !draft.startTime) {
            props.setFieldValue('resource.startDateTime', combineDateAndTime(draft.date, ALL_DAY_START_TIME));
            props.setFieldValue(
                'resource.endDateTime',
                draft.isAllDay ? combineDateAndTime(draft.date, ALL_DAY_END_TIME) : undefined,
            );
        } else {
            props.setFieldValue('resource.startDateTime', combineDateAndTime(draft.date, draft.startTime));
            props.setFieldValue(
                'resource.endDateTime',
                draft.endTime ? combineDateAndTime(draft.date, draft.endTime) : undefined,
            );
        }
        setIsOpen(false);
    };

    const label = formatWhenLabel(
        props.startDateTime,
        props.endDateTime,
        props.duration ? parseInt(props.duration) : null,
    );

    return (
        <>
            <PressableDynamicOpacity
                onPress={() => setIsOpen(true)}
                isDisabled={props.isDisabled}
                testID="visit-when-picker-trigger"
                style={{ width: '100%' }}
            >
                <View
                    style={{
                        width: '100%',
                        borderBottomWidth: 1,
                        borderBottomColor: colors.Dividers,
                        paddingVertical: getSpacing(3),
                        paddingHorizontal: getSpacing(2),
                    }}
                >
                    <Stack horizontal justify="space-between" align="center" fullWidth space={getSpacing(3)}>
                        <Stack vertical space={getSpacing(1.5)} flex={1}>
                            <Text variant="placeholder-text">{intl('when')}</Text>
                            {label ? <Text variant="input-text">{label}</Text> : null}
                        </Stack>
                        <Icon name={label ? 'Edit' : 'Calendar'} size="button-icon" colorVariant="Gray-Text" />
                    </Stack>
                </View>
            </PressableDynamicOpacity>
            {/* PromptContainer directly (not StandardPrompt): the drawer/form contexts this picker
                renders in have no NestedScrollViewPortalHostContainer, whose absence makes
                StandardPrompt throw. */}
            {/* Wider + taller on desktop, and the actions live OUTSIDE the scroll area so
                Set/Cancel stay pinned and visible even on 5-week months or short windows. */}
            <PromptContainer
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                mobileMaxHeight={'85%'}
                desktopWidth={480}
                desktopMaxHeight={'92%'}
                renderPromptContents={() => (
                    <Stack vertical fullWidth style={{ flexShrink: 1 }}>
                        <PRScrollView style={{ width: '100%', flexShrink: 1 }}>
                            <Stack vertical fullWidth space={contentStackSpacing} padding={getSpacing(6)}>
                                <Stack horizontal align="center" space={2.5} fullWidth>
                                    <Icon name="Calendar" size="button-icon" />
                                    <Text variant="h4">{intl('promptTitle')}</Text>
                                </Stack>
                                {renderPickerContents()}
                            </Stack>
                        </PRScrollView>
                        <Stack
                            vertical
                            fullWidth
                            style={{ paddingHorizontal: getSpacing(6), paddingBottom: getSpacing(6) }}
                        >
                            <PromptActions
                                actions={[
                                    {
                                        title: intl('set'),
                                        onPress: handleSet,
                                        isDisabled: !canSet,
                                        testID: 'visit-when-picker-set',
                                    },
                                    {
                                        title: intl('cancel'),
                                        variant: 'secondary',
                                        onPress: () => setIsOpen(false),
                                        testID: 'visit-when-picker-cancel',
                                    },
                                ]}
                            />
                        </Stack>
                    </Stack>
                )}
            />
        </>
    );

    function renderPickerContents() {
        return (
            <Stack vertical fullWidth space={getSpacing(4)}>
                {/* react-native-calendars renders at a fixed intrinsic width — center it */}
                <View style={{ width: '100%' }}>
                    <Calendar
                        initialDate={draft.date || undefined}
                        onDayPress={handleDayPress}
                        markedDates={markedDates}
                        theme={calendarTheme}
                        testID="visit-when-picker-calendar"
                        style={{ width: '100%', paddingLeft: 0, paddingRight: 0 }}
                    />
                </View>
                <Stack horizontal space={getSpacing(6)} align="center">
                    <Switch
                        title={intl('allDay')}
                        value={draft.isAllDay}
                        testID="visit-when-picker.allDay"
                        onChange={(value) =>
                            setDraft((prev) => ({
                                ...prev,
                                isAllDay: value,
                                startTime: value ? null : prev.startTime,
                                endTime: value ? null : prev.endTime,
                            }))
                        }
                    />
                </Stack>
                {showTimeSelects ? (
                    // Side-by-side on desktop so both times (and Set/Cancel below) fit without
                    // scrolling; stacked on mobile where the sheet is narrow.
                    <TimeFieldsLayout isMobile={isMobile}>
                        <Stack vertical flex={isMobile ? undefined : 1} fullWidth={isMobile}>
                            <TimeListField
                                title={intl('startTime')}
                                value={draft.startTime}
                                testID="visit-when-picker.startTime"
                                onChange={(value) =>
                                    setDraft((prev) => {
                                        const startTime = value;
                                        let endTime = prev.endTime;
                                        // Follow the start while the end is unset or no longer after it.
                                        if (startTime && (!endTime || endTime <= startTime)) {
                                            const defaultEnd = addMinutesToTimeSameDay(
                                                startTime,
                                                DEFAULT_VISIT_MINUTES,
                                            );
                                            // A clamped default can land on/before a late start — leave unset then.
                                            endTime = defaultEnd > startTime ? defaultEnd : null;
                                        }
                                        return { ...prev, startTime, endTime };
                                    })
                                }
                            />
                        </Stack>
                        {draft.startTime ? (
                            <Stack vertical flex={isMobile ? undefined : 1} fullWidth={isMobile}>
                                <TimeListField
                                    title={intl('endTime')}
                                    value={draft.endTime}
                                    testID="visit-when-picker.endTime"
                                    onChange={(value) => setDraft((prev) => ({ ...prev, endTime: value }))}
                                    error={endInvalid ? intl('endMustBeAfterStart') : undefined}
                                />
                            </Stack>
                        ) : null}
                    </TimeFieldsLayout>
                ) : null}
            </Stack>
        );
    }
};

export default React.memo(VisitWhenPicker);
